import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Mail, MapPin, Phone, Instagram, Facebook, Twitter, Linkedin, Youtube, Send } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLanguage } from "@/context/LanguageContext";
import api, { getPublicSettings } from "@/lib/api";

export function Footer() {
  const { t } = useLanguage();
  const [social, setSocial] = useState({});
  useEffect(() => { getPublicSettings().then(setSocial).catch(() => {}); }, []);
  const socials = [
    ["social_instagram", Instagram, "Instagram"], ["social_facebook", Facebook, "Facebook"],
    ["social_twitter", Twitter, "X / Twitter"], ["social_linkedin", Linkedin, "LinkedIn"],
    ["social_youtube", Youtube, "YouTube"], ["social_telegram", Send, "Telegram"],
  ].filter(([k]) => social[k]);
  const cols = [
    { h: t("footer_company"), links: [[t("footer_about"), "/knowledge"], [t("footer_products"), "/products"], [t("footer_refer"), "/refer"], [t("footer_top"), "/leaderboard"]] },
    { h: t("footer_support"), links: [[t("footer_help"), "/login"], [t("footer_contact"), "/knowledge"], [t("footer_faq"), "/#faq"], [t("footer_calculator"), "/tools"]] },
    { h: t("footer_legal"), links: [[t("footer_terms"), "/terms-of-service"], [t("footer_privacy"), "/privacy-policy"], [t("footer_refund"), "/terms-of-service"]] },
  ];
  const phone = social.support_phone || "9823567811";
  const wa = social.support_whatsapp || "919823567811";
  const email = social.support_email || "support@creditkendra.com";
  const address = social.support_address || "Credit Kendra, Virar East, Maharashtra 401305";
  const phoneFmt = phone.replace(/^91/, "").replace(/(\d{5})(\d{5})/, "$1 $2");
  return (
    <footer className="bg-[#0B223D] text-white mt-20">
      <div className="max-w-[1280px] mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <Logo height={42} light />
          <p className="mt-4 text-sm text-white/70 leading-relaxed max-w-xs">
            {t("footer_tagline")}
          </p>
          <div className="mt-5 space-y-2 text-sm">
            <a href={`tel:+91${phone.replace(/^91/, "")}`} data-testid="footer-call" className="flex items-center gap-2 text-white/80 hover:text-teal-400 transition-colors">
              <Phone className="w-4 h-4 text-teal-400" /> Call +91 {phoneFmt}
            </a>
            <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-2 text-white/80 hover:text-teal-400 transition-colors">
              <MessageCircle className="w-4 h-4 text-teal-400" /> +91 {wa.replace(/^91/, "").replace(/(\d{5})(\d{5})/, "$1 $2")} (WhatsApp)
            </a>
            <a href={`mailto:${email}`} data-testid="footer-email" className="flex items-center gap-2 text-white/80 hover:text-teal-400 transition-colors">
              <Mail className="w-4 h-4 text-teal-400" /> {email}
            </a>
            <div className="flex items-start gap-2 text-white/70" data-testid="footer-address">
              <MapPin className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" />
              <span>{address}</span>
            </div>
            {socials.length > 0 && (
              <div className="flex gap-2.5 pt-3" data-testid="footer-socials">
                {socials.map(([k, Icon, name]) => (
                  <a key={k} href={social[k]} target="_blank" rel="noreferrer" aria-label={name} data-testid={`footer-social-${k.replace("social_", "")}`}
                    className="w-9 h-9 rounded-full bg-white/10 hover:bg-teal-500 flex items-center justify-center transition-colors">
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
        {cols.map((c) => (
          <div key={c.h}>
            <h4 className="font-display font-bold text-sm tracking-wide text-teal-400 mb-4">{c.h}</h4>
            <ul className="space-y-2.5">
              {c.links.map(([label, to]) => (
                <li key={label}>
                  <Link to={to} className="text-sm text-white/70 hover:text-white transition-colors">{label}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1280px] mx-auto px-6 py-5">
          <p className="text-xs text-white/50 leading-relaxed" data-testid="footer-disclaimer">{t("footer_disclaimer")}</p>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1280px] mx-auto px-6 py-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/50">
          <p>© {new Date().getFullYear()} {t("footer_rights")}</p>
          <p className="max-w-xl">Financial products are offered by partner institutions. Rewards are subject to verified conversions and campaign terms. Please invest/borrow responsibly.</p>
        </div>
      </div>
    </footer>
  );
}
